import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  CreditCard, AlertCircle, CheckCircle2,
  Users, Trophy, ShoppingBag, CalendarDays, Store,
  QrCode, BarChart3, Smartphone, Layers,
} from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const problems = [
  { icon: AlertCircle, text: 'Swish utan struktur — ingen vet vad som sålts' },
  { icon: AlertCircle, text: 'Ingen koll på försäljning per kassa eller säljpunkt' },
  { icon: AlertCircle, text: 'Flera säljpunkter på samma event skapar kaos' },
];

const solutions = [
  { icon: BarChart3,   text: 'Försäljning per kiosk — se vad varje kassa säljer i realtid' },
  { icon: Layers,      text: 'Automatisk summering per kassa och event' },
  { icon: CheckCircle2,text: 'Export till Excel med ett klick efter eventet' },
  { icon: QrCode,      text: 'Unikt Swish QR per kassa — alltid rätt summering' },
  { icon: Smartphone,  text: 'Fungerar i mobilen — ingen extra hårdvara behövs' },
  { icon: CreditCard,  text: 'Automatiska kvittenser till köpare' },
];

const audiences = [
  { icon: Users,        label: 'Idrottsföreningar', desc: 'Föreningskassan, cuper och säsongsavgifter.' },
  { icon: Trophy,       label: 'Cuper & tävlingar',  desc: 'Startavgifter, lotter och kiosk på plats.' },
  { icon: ShoppingBag,  label: 'Kiosker',             desc: 'Ta betalt snabbt utan terminal.' },
  { icon: CalendarDays, label: 'Event & fester',      desc: 'Entrébiljetter, bar och programavgifter.' },
  { icon: Store,        label: 'Marknadsknallar',     desc: 'Enkelt kassasystem för varje stånd.' },
];

const SwishkassaPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleCapture = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) setSubmitted(true);
    // TODO: connect to Supabase or EmailJS to store email
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>Swishkassa för föreningar & event — Aifinity</title>
        <meta name="description" content="En komplett kassalösning byggd kring Swish. Automatiserade summor per kiosk, realtidsöversikt och exportera till Excel efter eventet — för svenska föreningar och arrangörer." />
        <meta property="og:url" content="https://aifinity.se/swishkassa" />
        <link rel="canonical" href="https://aifinity.se/swishkassa" />
      </Helmet>
      <CorporateNavbar />

      <LightPageHero
        badge="Swishkassa · Coming Soon"
        heading={<>En enkel kassalösning för<br /><span style={{ background: 'linear-gradient(135deg, #34d399 0%, #059669 50%, #0d9488 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>föreningar och event</span></>}
        subtext="Hantera kiosk, entré och lotter på ett ställe. Swish QR per kassa, automatisk summering, ingen krånglig setup."
        ctaLabel="Anmäl intresse"
        ctaHref="/contact"
      />

      {/* ── Problem ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-14"
            >
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600 mb-4">Problemet</span>
              <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
                Swish fungerar — men saknar kassa
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {problems.map(({ icon: Icon, text }, i) => (
                <motion.div
                  key={text}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-4 items-start bg-red-50 rounded-2xl border border-red-100 p-6"
                >
                  <Icon className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-[15px] text-text-primary font-medium leading-snug">{text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Lösning ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-14"
            >
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600 mb-4">Lösningen</span>
              <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
                Se exakt vad varje kiosk säljer i realtid
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {solutions.map(({ icon: Icon, text }, i) => (
                <motion.div
                  key={text}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-4 items-start bg-white rounded-2xl border border-border-light p-6 shadow-sm hover:border-emerald-200 transition-colors"
                >
                  <Icon className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                  <p className="text-[15px] text-text-primary font-medium leading-snug">{text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Vem passar det? ── */}
      <section className="py-20 bg-white border-t border-border-light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-14"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600 mb-4">Passar för</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">
              Vem behöver Swishkassa?
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {audiences.map(({ icon: Icon, label, desc }, i) => (
              <motion.div
                key={label}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl border border-border-light p-5 text-center shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all duration-300"
              >
                <div className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 items-center justify-center mb-3 shadow-md">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-[13px] font-bold text-text-primary mb-1">{label}</h3>
                <p className="text-[11px] text-text-secondary leading-snug">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-md mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 mb-6">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse inline-block" />
              Under utveckling — lansering planerad 2025
            </div>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-4">
              Vill du vara med från start?
            </h2>
            <p className="text-text-secondary mb-8 leading-relaxed">
              Anmäl intresse så kontaktar vi dig när Swishkassa är redo för betatestare.
            </p>

            {/* ── Email capture — primary CTA ── */}
            {submitted ? (
              <div className="flex items-center justify-center gap-2 text-emerald-600 text-base font-semibold py-4 px-6 bg-emerald-50 rounded-2xl border border-emerald-200">
                <CheckCircle2 className="w-5 h-5" />
                Tack! Vi hör av oss när betatestning startar.
              </div>
            ) : (
              <form onSubmit={handleCapture} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="din@email.se"
                  className="flex-1 border border-border-light rounded-xl px-5 py-4 text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-300 transition-colors"
                />
                <button
                  type="submit"
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold px-6 py-4 rounded-xl text-base hover:opacity-90 transition-opacity whitespace-nowrap shadow-lg shadow-emerald-500/25"
                >
                  Få tidig access
                </button>
              </form>
            )}
            <p className="text-xs text-text-muted mt-3">Vi väljer ut beta-användare löpande</p>
          </motion.div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default SwishkassaPage;
